/* oxlint-disable typescript/no-explicit-any -- batching requires generic operation handling */
/* oxlint-disable typescript/no-non-null-assertion -- runtime guarantees array access */

// ============================================================================
// Core DataLoader Types
// ============================================================================

type BatchItem<TKey, TValue> = {
  key: TKey
  aborted: boolean
  resolve: ((value: TValue) => void) | null
  reject: ((error: Error) => void) | null
  batch: Batch<TKey, TValue> | null
}

type Batch<TKey, TValue> = {
  items: BatchItem<TKey, TValue>[]
}

export type BatchLoader<TKey, TValue> = {
  validate: (keys: TKey[]) => boolean
  fetch: (keys: TKey[]) => Promise<TValue[] | Promise<TValue>[]>
}

export type DataLoaderConfig = {
  timeoutMs?: number // Default: 0 (next tick)
}

// ============================================================================
// DataLoader Implementation
// ============================================================================

export function createDataLoader<TKey, TValue>(
  batchLoader: BatchLoader<TKey, TValue>,
  config?: DataLoaderConfig,
) {
  type Item = BatchItem<TKey, TValue>
  type BatchType = Batch<TKey, TValue>

  const timeoutMs = config?.timeoutMs ?? 0

  let pendingItems: Item[] | null = null
  let dispatchTimer: ReturnType<typeof setTimeout> | null = null

  const destroyTimerAndPendingItems = () => {
    if (dispatchTimer !== null) clearTimeout(dispatchTimer)
    dispatchTimer = null
    pendingItems = null
  }

  function groupItems(items: Item[]): Item[][] {
    const groupedItems: Item[][] = [[]]
    let index = 0

    while (true) {
      const item = items[index]
      if (!item) break

      const lastGroup = groupedItems.at(-1)!

      if (item.aborted) {
        item.reject?.(new Error('Aborted'))
        index++
        continue
      }

      const isValid = batchLoader.validate(
        lastGroup.concat(item).map((it) => it.key),
      )

      if (isValid) {
        lastGroup.push(item)
        index++
        continue
      }

      if (lastGroup.length === 0) {
        item.reject?.(new Error('Input is too big for a single dispatch'))
        index++
        continue
      }

      groupedItems.push([])
    }

    return groupedItems
  }

  function dispatch() {
    const items = pendingItems!
    destroyTimerAndPendingItems()

    // If only one item, skip batching validation and fetch directly
    if (items.length === 1) {
      const item = items[0]

      if (item.aborted) {
        item.reject?.(new Error('Aborted'))
        return
      }

      const promise = batchLoader.fetch([item.key])

      promise
        .then(async (result) => {
          try {
            const value = await Promise.resolve(result[0])
            item.resolve?.(value)
          } catch (cause) {
            item.reject?.(cause as Error)
          }
          return null
        })
        .catch((cause: unknown) => {
          item.reject?.(cause as Error)
        })

      return
    }

    // Multiple items - use grouping logic
    const groupedItems = groupItems(items)

    for (const items of groupedItems) {
      if (items.length === 0) continue

      const batch: BatchType = { items }

      for (const item of items) {
        item.batch = batch
      }

      const promise = batchLoader.fetch(batch.items.map((item) => item.key))

      promise
        .then(async (result) => {
          await Promise.all(
            result.map(async (valueOrPromise, index) => {
              const item = batch.items[index]
              try {
                const value = await Promise.resolve(valueOrPromise)
                item.resolve?.(value)
              } catch (cause) {
                item.reject?.(cause as Error)
              }

              item.batch = null
              item.reject = null
              item.resolve = null
            }),
          )

          for (const item of batch.items) {
            item.reject?.(new Error('Missing result'))
            item.batch = null
          }
          return null
        })
        .catch((cause: unknown) => {
          for (const item of batch.items) {
            item.reject?.(cause as Error)
            item.batch = null
          }
        })
    }
  }

  function load(key: TKey): Promise<TValue> {
    const item: Item = {
      aborted: false,
      key,
      batch: null,
      resolve: null,
      reject: null,
    }

    const promise = new Promise<TValue>((resolve, reject) => {
      item.reject = reject
      item.resolve = resolve

      pendingItems ??= []
      pendingItems.push(item)
    })

    dispatchTimer ??= setTimeout(dispatch, timeoutMs)

    return promise
  }

  return { load }
}
